// src/features/admin/categoryApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  Category,
  ApiResponse,
  CategoryListParams,
  CategoryCountParams,
  CategoryDetailParams,
  CategoryDeleteParams,
  AddCategoryRequest,
  UpdateCategoryRequest,
  API_CONFIG,
} from '../../features/admin/types.ts';

export const categoryApi = createApi({
  reducerPath: 'categoryApi',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_SERVER_ENDPOINT,
    prepareHeaders: (headers, { endpoint }) => {
      console.log('Preparing headers for endpoint:', endpoint);
      
      // ALWAYS set authkey first
      headers.set(
        "authkey",
        "7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3"
      );
      
      // Try all token keys - check admin_token first, then vendor_token, then auth_token
      const adminToken = localStorage.getItem('admin_token');
      const vendorToken = localStorage.getItem('vendor_token');
      const authToken = localStorage.getItem('auth_token');
      const token = adminToken || vendorToken || authToken;
      
      console.log('Admin Token:', adminToken ? 'exists' : 'missing');
      console.log('Auth Token:', authToken ? 'exists' : 'missing');
      console.log('Using Token:', token ? 'exists' : 'MISSING - THIS IS THE PROBLEM!');
      
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      } else {
        console.error('⚠️ NO TOKEN FOUND IN LOCALSTORAGE!');
      }
      
      // Handle Content-Type based on endpoint
      if (endpoint === 'addCategory' || endpoint === 'updateCategory') {
        // For FormData requests, don't set Content-Type
        // Let the browser handle it automatically with proper boundary
        headers.delete('Content-Type');
      } else {
        // For all other endpoints, use JSON
        if (!headers.has('Content-Type')) {
          headers.set("Content-Type", "application/json");
        }
      }
      
      console.log('Final headers:', Object.fromEntries(headers.entries()));
      
      return headers;
    },
  }),
  tagTypes: ['Category'],
  endpoints: (build) => ({
    // 1. List Categories
    listCategories: build.query<ApiResponse<Category[]>, CategoryListParams>({
      query: ({ page = 1, limit = 10, search_str = '' } = {}) => ({
        url: API_CONFIG.ENDPOINTS.CATEGORY_LIST,
        method: 'POST',
        body: {
          start: (page - 1) * limit,
          limit,
          ...(search_str && { search_str })
        },
      }),
      providesTags: ['Category'],
      transformResponse: (response: any) => {
        console.log('List Categories Response:', response);
        return {
          error: response.error || false,
          message: response.message || '',
          result: response.result || response.data || [],
          count: response.count || response.total || response.total_records || 0,
        };
      },
    }),

    // 2. Add Category
    addCategory: build.mutation<ApiResponse<any>, AddCategoryRequest>({
      query: (data) => {
        console.log('Add Category Data:', data);
        
        const formData = new FormData();
        
        // Append required fields
        formData.append('cat_name', data.cat_name.trim());
        formData.append('cat_short_name', data.cat_short_name.trim());
        
        // Append optional fields only if they have values
        if (data.cat_desc && data.cat_desc.trim()) {
          formData.append('cat_desc', data.cat_desc.trim());
        }
        
        // Always append status, default to 1 if not provided
        formData.append('status', (data.status ?? 1).toString());
        // 1 = listed in the shop, 0 = hidden festival / offer category
        formData.append('show_in_list', (data.show_in_list ?? 1).toString());
        
        // Append image file if provided
        if (data.cat_main_image instanceof File) {
          formData.append('cat_main_image', data.cat_main_image);
        }

        // Debug: Log form data contents
        console.log('Add Category FormData entries:');
        for (let [key, value] of formData.entries()) {
          console.log(key, value);
        }

        return {
          url: API_CONFIG.ENDPOINTS.CATEGORY_ADD,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Category'],
    }),

    // 3. Update Category
    updateCategory: build.mutation<ApiResponse<any>, UpdateCategoryRequest>({
      query: (data) => {
        console.log('Update Category Data:', data);
        
        const formData = new FormData();
        
        // Append required fields
        formData.append('cat_id', data.cat_id.toString());
        formData.append('cat_name', data.cat_name.trim());
        formData.append('cat_short_name', data.cat_short_name.trim());
        
        // Append optional fields only if they have values
        if (data.cat_desc && data.cat_desc.trim()) {
          formData.append('cat_desc', data.cat_desc.trim());
        }
        
        // Always append status
        formData.append('status', (data.status ?? 1).toString());
        formData.append('show_in_list', (data.show_in_list ?? 1).toString());
        
        // Append image file if provided - if no image is provided, the server should keep the existing one
        if (data.cat_main_image instanceof File) {
          formData.append('cat_main_image', data.cat_main_image);
        }

        // Debug: Log form data contents
        console.log('Update Category FormData entries:');
        for (let [key, value] of formData.entries()) {
          console.log(key, value);
        }

        return {
          url: API_CONFIG.ENDPOINTS.CATEGORY_UPDATE,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Category'],
    }), 

    // 4. Delete Category - FIXED
    deleteCategory: build.mutation<ApiResponse<any>, CategoryDeleteParams>({
      query: ({ cat_id }) => {
        console.log('Delete Category - cat_id:', cat_id);
        
        // Send as JSON instead of URLSearchParams
        return {
          url: API_CONFIG.ENDPOINTS.CATEGORY_DELETE,
          method: 'POST',
          body: { cat_id: cat_id.toString() },
        };
      },
      invalidatesTags: ['Category'],
      transformResponse: (response: any) => {
        console.log('Delete Category Response:', response);
        return response;
      },
      transformErrorResponse: (response: any) => {
        console.error('Delete Category Error Response:', response);
        return {
          status: response.status,
          data: response.data || { message: 'Unknown error occurred' }
        };
      },
    }),

    // 5. Get Category Count
    getCategoryCount: build.query<ApiResponse<{ count: number }>, CategoryCountParams>({
      query: ({ search_str = '' } = {}) => ({
        url: API_CONFIG.ENDPOINTS.CATEGORY_COUNT,
        method: 'POST',
        body: {
          ...(search_str && { search_str })
        },
      }),
      providesTags: ['Category'],
      transformResponse: (response: any) => {
        console.log('Category Count Response:', response);
        return response;
      },
    }),

    // 6. Get Category Details
    getCategoryDetail: build.query<ApiResponse<Category>, CategoryDetailParams>({
      query: ({ cat_id }) => ({
        url: API_CONFIG.ENDPOINTS.CATEGORY_DETAIL,
        method: 'POST',
        body: { cat_id: cat_id.toString() },
      }),
      providesTags: ['Category'],
    }),

    // 7. Get Category by Name (localhost API) - FIXED to use form-urlencoded
    getCategoryByName: build.mutation<ApiResponse<Category>, { cat_name: string }>({
      query: ({ cat_name }) => ({
        url: `${API_CONFIG.LOCALHOST_BASE_URL}/catlistnameCount`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: `appauthkey=${encodeURIComponent(API_CONFIG.APP_AUTH_KEY)}&cat_name=${encodeURIComponent(cat_name)}`,
      }),
    }),

    // 8. Search Categories by Name (localhost API) - FIXED to use form-urlencoded
    searchCategoriesByName: build.query<ApiResponse<Category[]>, { search_str?: string; start?: number; limit?: number }>({
      query: ({ search_str = '', start = 0, limit = 10 } = {}) => {
        const params = new URLSearchParams({
          appauthkey: API_CONFIG.APP_AUTH_KEY,
          search_str,
          start: start.toString(),
          limit: limit.toString(),
        });
        
        return {
          url: `${API_CONFIG.LOCALHOST_BASE_URL}/catlistallname`,
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: params.toString(), // Convert to string
        };
      },
    }),

    // 9. Get Search Categories Count (localhost API) - FIXED to use form-urlencoded
    getSearchCategoriesCount: build.query<ApiResponse<{ total_records: number }>, { search_str?: string }>({
      query: ({ search_str = '' } = {}) => {
        const params = new URLSearchParams({
          appauthkey: API_CONFIG.APP_AUTH_KEY,
          search_str,
        });
        
        return {
          url: `${API_CONFIG.LOCALHOST_BASE_URL}/catlistallnameCount`,
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: params.toString(), // Convert to string
        };
      },
    }),
  }),
});

export const {
  useListCategoriesQuery,
  useAddCategoryMutation,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,
  useGetCategoryCountQuery,
  useGetCategoryDetailQuery,
  useGetCategoryByNameMutation,
  useSearchCategoriesByNameQuery,
  useGetSearchCategoriesCountQuery,
} = categoryApi;